import { getCSRFToken } from "./htmx-helpers";

// Remote address-search combobox backing `templates/components/address_search.templ`.
//
// Debounces keystrokes 250ms and requires >=3 characters before calling the
// geocode autocomplete proxy (`GET /api/geocode/autocomplete?q=`), matching the
// proxy's own short-circuit for short queries (internal/api/handler_geocode.go).
// A raw `fetch()` here is NOT covered by HTMX's automatic CSRF header
// injection, so the token is read from the `<meta name="csrf-token">` tag and
// sent explicitly (see CLAUDE.md "CSRF Tokens on fetch() / Alpine").
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 3;
const AUTOCOMPLETE_URL = "/api/geocode/autocomplete";

export interface AddressSuggestion {
  formatted: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  // Localized (native-language, since the geocode endpoints no longer force
  // lang=de) display name — shown only inside `formatted`, never persisted.
  country: string;
  // ISO-3166-1 alpha-2 code — the field wizard-autosave.ts actually persists
  // onto domain.Property.Country. See handler_geocode.go's
  // AddressSuggestionResult.CountryCode doc comment.
  country_code: string;
  lat: number;
  lng: number;
  result_type: string;
  match_type: string;
}

interface AutocompleteResponse {
  results?: AddressSuggestion[];
}

export function addressSearchState() {
  return {
    query: "",
    results: [] as AddressSuggestion[],
    open: false,
    loading: false,
    _debounceTimer: null as ReturnType<typeof setTimeout> | null,
    _abortController: null as AbortController | null,
    _reposition: null as null | (() => void),

    init() {
      // Reposition the menu whenever the page/containers scroll or the window
      // resizes while it's open (the input may move). Bound once so the same
      // reference can be add/removeEventListener'd.
      this._reposition = () => this._position();
      // Drive the menu's open/close side effects (top-layer popover promotion
      // + positioning) off the `open` flag.
      (this as any).$watch("open", (v: boolean) => this._onOpenChange(v));
      // Re-anchor when the result set (and thus the menu's height / flip
      // decision) changes while open. $nextTick so the DOM has the new rows.
      (this as any).$watch("results", () => {
        if (this.open) (this as any).$nextTick(() => this._position());
      });
    },

    _onOpenChange(open: boolean) {
      const menu = (this as any).$refs?.menu as HTMLElement | undefined;
      if (!menu) return;
      if (open) {
        // Promote into the top layer so the menu escapes ancestor
        // overflow/transform clipping and paints above a modal <dialog>. No-op
        // if the browser lacks the Popover API (attr never set by x-init).
        if (menu.hasAttribute("popover") && !menu.matches(":popover-open")) {
          try { menu.showPopover(); } catch { /* already open */ }
        }
        this._position();
        (this as any).$nextTick(() => this._position()); // refine once it has a measurable box
        window.addEventListener("scroll", this._reposition!, true);
        window.addEventListener("resize", this._reposition!);
      } else {
        window.removeEventListener("scroll", this._reposition!, true);
        window.removeEventListener("resize", this._reposition!);
        if (menu.hasAttribute("popover") && menu.matches(":popover-open")) {
          try { menu.hidePopover(); } catch { /* already closed */ }
        }
      }
    },

    // Anchor the menu under (or, near the viewport bottom, above) the input
    // using position: fixed with VIEWPORT coordinates. A top-layer popover's
    // containing block is the viewport, so viewport coords are correct whether
    // or not the popover is inside a transformed/positioned ancestor (e.g. the
    // modal-box). This replaces @alpinejs/anchor, whose absolute strategy emits
    // offsetParent-relative coordinates that a top-layer element mis-resolves
    // against the viewport (the ~offsetParent-offset displacement bug).
    _position() {
      const input = (this as any).$refs?.input as HTMLElement | undefined;
      const menu = (this as any).$refs?.menu as HTMLElement | undefined;
      if (!input || !menu) return;
      const r = input.getBoundingClientRect();
      const gap = 4;
      menu.style.position = "fixed";
      menu.style.width = r.width + "px";
      menu.style.left = r.left + "px";
      const mh = menu.offsetHeight;
      const spaceBelow = window.innerHeight - r.bottom;
      // Flip above the input when there isn't room below AND there's more room
      // above; otherwise anchor below.
      if (mh > 0 && spaceBelow < mh + gap && r.top > spaceBelow) {
        menu.style.top = Math.max(gap, r.top - mh - gap) + "px";
      } else {
        menu.style.top = r.bottom + gap + "px";
      }
    },

    onInput() {
      if (this._debounceTimer) clearTimeout(this._debounceTimer);

      const q = this.query.trim();
      if (q.length < MIN_QUERY_LENGTH) {
        // Below the proxy's own minimum — don't call it, and don't show a
        // stale dropdown while the user is still typing the first few chars.
        this.open = false;
        this.loading = false;
        this.results = [];
        if (this._abortController) this._abortController.abort();
        return;
      }

      this.open = true;
      this.loading = true;
      this._debounceTimer = setTimeout(() => this.fetchResults(q), DEBOUNCE_MS);
    },

    async fetchResults(q: string) {
      // Identity-based staleness guard: if a newer request has already
      // replaced `_abortController` by the time this one's promise settles,
      // every branch below is a no-op instead of clobbering fresher state.
      const controller = new AbortController();
      if (this._abortController) this._abortController.abort();
      this._abortController = controller;

      try {
        const resp = await fetch(`${AUTOCOMPLETE_URL}?q=${encodeURIComponent(q)}`, {
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          signal: controller.signal,
        });

        if (this._abortController !== controller) return;

        if (!resp.ok) {
          this.results = [];
          return;
        }

        const data = (await resp.json()) as AutocompleteResponse;
        if (this._abortController !== controller) return;
        this.results = Array.isArray(data?.results) ? data.results : [];
      } catch (e: any) {
        if (e?.name !== "AbortError" && this._abortController === controller) {
          this.results = [];
        }
      } finally {
        if (this._abortController === controller) {
          this.loading = false;
        }
      }
    },

    select(result: AddressSuggestion) {
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      if (this._abortController) this._abortController.abort();

      this.query = result.formatted;
      this.open = false;
      this.loading = false;
      this.results = [];

      // Dispatch from $root (this combobox's own root div) rather than the
      // triggering <li>: the consuming wrapper (propertyLocationAutosave /
      // addAccommodationLocation) listens on an ANCESTOR of $root, and the menu
      // (now inline, not teleported) is a descendant of $root, so a bubbling
      // event from $root reaches the wrapper on a clean DOM path.
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(new CustomEvent("address-selected", { detail: result, bubbles: true, composed: true }));
    },

    close() {
      this.open = false;
    },

    onFocus() {
      if (this.query.trim().length >= MIN_QUERY_LENGTH && this.results.length > 0) {
        this.open = true;
      }
    },
  };
}

export function registerAddressSearch(alpine: any) {
  alpine.data("addressSearch", addressSearchState);
}
